import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Download, Filter, BarChart3, PieChart as PieChartIcon,
  Activity, Loader2, DollarSign, FolderOpen, AlertTriangle, CheckCircle, FileText,
} from 'lucide-react';
import { useBudgets } from '../src/hooks/queries/use-budgets';
import { useProjects } from '../src/hooks/queries/use-projects';
import { useChangeOrders } from '../src/hooks/queries/use-change-orders';
import { useAuth } from '../src/hooks/useAuth';
import { useOrgCurrency } from '../src/hooks/useOrgCurrency';
import { formatCurrency } from '../src/utils/currency';
import { pdfUtils } from '../src/utils/pdf.utils';
import { showError, showSuccess } from '../src/utils/notification.utils';

const CHART_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

function shortAmount(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(Math.round(v));
}

export function ReportingDashboard() {
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();

  const { data: rawBudgets = [], isLoading: budgetsLoading } = useBudgets(orgId);
  const { data: rawProjects = [] } = useProjects(orgId);
  const { data: rawChangeOrders = [] } = useChangeOrders(orgId);

  const budgets = rawBudgets as any[];
  const projects = rawProjects as any[];
  const changeOrders = rawChangeOrders as any[];

  // ── Filters ────────────────────────────────────────────────────────────────
  const [projectFilter, setProjectFilter] = useState('all');
  const [isCumulative, setIsCumulative] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportingReport, setExportingReport] = useState(false);
  const [exportingCategory, setExportingCategory] = useState(false);

  const getProjectName = (projectId: string) =>
    projects.find((p: any) => p.project_id === projectId)?.project_name ?? projectId;

  // CO adjustments by budget
  const coAdjByBudget = useMemo(() => {
    return changeOrders.reduce((acc: Record<string, number>, co: any) => {
      if (!co.budget_id || co.status !== 'approved') return acc;
      acc[co.budget_id] = (acc[co.budget_id] ?? 0) + (co.cost_impact ?? 0);
      return acc;
    }, {});
  }, [changeOrders]);

  // Filtered budgets
  const filteredBudgets = useMemo(() => {
    let result = budgets;
    if (projectFilter !== 'all') result = result.filter((b: any) => b.project_id === projectFilter);
    if (!isCumulative) {
      if (dateFrom) result = result.filter((b: any) => new Date(b.created_at) >= new Date(dateFrom));
      if (dateTo) result = result.filter((b: any) => new Date(b.created_at) <= new Date(dateTo + 'T23:59:59'));
    }
    return result;
  }, [budgets, projectFilter, isCumulative, dateFrom, dateTo]);

  // ── Aggregates ─────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const totalBudget = filteredBudgets.reduce((s: number, b: any) => s + (b.original_amount || 0), 0);
    const totalActual = filteredBudgets.reduce((s: number, b: any) => s + (b.actual_amount || 0), 0);
    const totalCommitted = filteredBudgets.reduce((s: number, b: any) => s + (b.committed_amount || 0), 0);
    const utilization = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;
    const variance = totalBudget - totalActual;
    const approved = filteredBudgets.filter((b: any) => b.status?.toLowerCase() === 'approved').length;
    return { totalBudget, totalActual, totalCommitted, utilization, variance, approved, count: filteredBudgets.length };
  }, [filteredBudgets]);

  // Monthly trends (grouped by creation month)
  const monthlyTrends = useMemo(() => {
    const months: Record<string, { month: string; sortKey: string; budget: number; actual: number; committed: number }> = {};
    filteredBudgets.forEach((b: any) => {
      const d = new Date(b.created_at);
      const sortKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      if (!months[sortKey]) months[sortKey] = { month: label, sortKey, budget: 0, actual: 0, committed: 0 };
      months[sortKey].budget += b.original_amount || 0;
      months[sortKey].actual += b.actual_amount || 0;
      months[sortKey].committed += b.committed_amount || 0;
    });
    return Object.values(months).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [filteredBudgets]);

  // Cumulative running total
  const cumulativeTrend = useMemo(() => {
    const sorted = [...filteredBudgets].sort(
      (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    let cumBudget = 0, cumActual = 0;
    const months: Record<string, { month: string; sortKey: string; cumBudget: number; cumActual: number }> = {};
    sorted.forEach((b: any) => {
      const d = new Date(b.created_at);
      const sortKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      cumBudget += b.original_amount || 0;
      cumActual += b.actual_amount || 0;
      months[sortKey] = { month: label, sortKey, cumBudget, cumActual };
    });
    return Object.values(months).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [filteredBudgets]);

  // Per-project performance
  const projectPerformance = useMemo(() => {
    const byProject: Record<string, { id: string; name: string; budget: number; actual: number; committed: number; count: number }> = {};
    filteredBudgets.forEach((b: any) => {
      const pid = b.project_id;
      if (!byProject[pid]) byProject[pid] = { id: pid, name: getProjectName(pid), budget: 0, actual: 0, committed: 0, count: 0 };
      byProject[pid].budget += (b.original_amount || 0) + (coAdjByBudget[b.budget_id] || 0);
      byProject[pid].actual += b.actual_amount || 0;
      byProject[pid].committed += b.committed_amount || 0;
      byProject[pid].count += 1;
    });
    return Object.values(byProject).sort((a, b) => b.budget - a.budget);
  }, [filteredBudgets, coAdjByBudget]);

  // Spending by project for pie chart
  const categoryData = useMemo(() => {
    const total = projectPerformance.reduce((s, p) => s + p.actual, 0);
    return projectPerformance
      .filter(p => p.actual > 0)
      .map((p, i) => ({
        name: p.name,
        amount: p.actual,
        value: total > 0 ? Math.round((p.actual / total) * 100) : 0,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
  }, [projectPerformance]);

  // Risk: budgets near or over limit
  const riskData = useMemo(() => {
    return filteredBudgets
      .map((b: any) => {
        const adj = (b.original_amount || 0) + (coAdjByBudget[b.budget_id] || 0);
        const pct = adj > 0 ? Math.min(120, ((b.actual_amount || 0) / adj) * 100) : 0;
        return {
          name: b.budget_name,
          project: getProjectName(b.project_id),
          pct,
          level: pct >= 100 ? 'Over Budget' : pct >= 90 ? 'High' : pct >= 75 ? 'Medium' : 'Low',
        };
      })
      .sort((a: any, b: any) => b.pct - a.pct)
      .slice(0, 10);
  }, [filteredBudgets, coAdjByBudget]);

  // ── Exports ────────────────────────────────────────────────────────────────

  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      const doc = pdfUtils.createPDF({
        title: 'Reports & Analytics',
        subtitle: `${isCumulative ? 'Cumulative Report' : `${dateFrom || '—'} to ${dateTo || '—'}`} · ${filteredBudgets.length} budget(s)`,
        date: new Date().toLocaleDateString(),
      });

      let y = 70;
      y = pdfUtils.addSectionHeader(doc, 'Financial Summary', y);
      y = pdfUtils.addKeyValueGrid(doc, [
        { label: 'Total Budget', value: formatCurrency(kpis.totalBudget, orgCurrency) },
        { label: 'Actual Spend', value: formatCurrency(kpis.totalActual, orgCurrency) },
        { label: 'Total Committed', value: formatCurrency(kpis.totalCommitted, orgCurrency) },
        { label: 'Budget Utilization', value: kpis.utilization.toFixed(1) + '%' },
        { label: 'Variance', value: (kpis.variance >= 0 ? '+' : '') + formatCurrency(Math.abs(kpis.variance), orgCurrency) },
        { label: 'Budgets', value: String(kpis.count) },
      ], y, 2);
      y += 8;

      if (projectPerformance.length > 0) {
        y = pdfUtils.addSectionHeader(doc, 'Project Performance', y);
        pdfUtils.addTable(
          doc,
          ['Project', '# Budgets', 'Total Budget', 'Actual Spend', 'Committed', 'Variance', '% Used'],
          projectPerformance.map(p => {
            const v = p.budget - p.actual;
            return [
              p.name,
              String(p.count),
              formatCurrency(p.budget, orgCurrency),
              formatCurrency(p.actual, orgCurrency),
              formatCurrency(p.committed, orgCurrency),
              (v >= 0 ? '+' : '') + formatCurrency(Math.abs(v), orgCurrency),
              p.budget > 0 ? (p.actual / p.budget * 100).toFixed(0) + '%' : '0%',
            ];
          }),
          y,
        );
      }

      await pdfUtils.exportToPDF(doc, `analytics_report_${new Date().toISOString().slice(0, 10)}.pdf`, { title: 'Analytics Report' });
      showSuccess('Report exported');
    } catch {
      showError('Failed to export report');
    } finally {
      setExportingReport(false);
    }
  };

  const handleExportCategoryPDF = async () => {
    setExportingCategory(true);
    try {
      const doc = pdfUtils.createPDF({
        title: 'Budget Spending by Project',
        subtitle: `Generated ${new Date().toLocaleDateString()}`,
        date: new Date().toLocaleDateString(),
      });

      let y = 70;
      y = pdfUtils.addSectionHeader(doc, 'Spending by Project', y);
      pdfUtils.addTable(
        doc,
        ['Project', 'Actual Spend', '% of Total'],
        categoryData.map(c => [c.name, formatCurrency(c.amount, orgCurrency), c.value + '%']),
        y,
      );

      await pdfUtils.exportToPDF(doc, `category_report_${new Date().toISOString().slice(0, 10)}.pdf`, { title: 'Category Report' });
      showSuccess('Category report exported');
    } catch {
      showError('Failed to export category report');
    } finally {
      setExportingCategory(false);
    }
  };

  const isLoading = budgetsLoading;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Reports &amp; Analytics</h2>
          <p className="text-muted-foreground">Financial analytics and KPI visualizations</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Cumulative / Date Range toggle */}
          <div className="inline-flex rounded-lg border border-border p-1 gap-1">
            <button
              onClick={() => setIsCumulative(true)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${isCumulative ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Cumulative
            </button>
            <button
              onClick={() => setIsCumulative(false)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${!isCumulative ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Date Range
            </button>
          </div>

          {!isCumulative && (
            <>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 text-sm" />
              <span className="text-muted-foreground text-sm">to</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 text-sm" />
            </>
          )}

          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-44">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((p: any) => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleExportReport} disabled={exportingReport || isLoading}>
            {exportingReport
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            Export Report
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="h-24 animate-pulse bg-muted rounded-lg mt-4" /></Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <DollarSign className="h-4 w-4 text-blue-600" />Total Budget
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-700">{formatCurrency(kpis.totalBudget, orgCurrency)}</div>
              <p className="text-xs text-blue-600 mt-1">{kpis.count} budget(s)</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-orange-600" />Actual Spend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-700">{formatCurrency(kpis.totalActual, orgCurrency)}</div>
              <p className="text-xs text-orange-600 mt-1">{kpis.utilization.toFixed(1)}% utilization</p>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${kpis.variance >= 0 ? 'from-green-50 to-green-100 border-green-200' : 'from-red-50 to-red-100 border-red-200'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                {kpis.variance >= 0
                  ? <CheckCircle className="h-4 w-4 text-green-600" />
                  : <AlertTriangle className="h-4 w-4 text-red-600" />}
                Variance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${kpis.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {kpis.variance >= 0 ? '+' : ''}{formatCurrency(Math.abs(kpis.variance), orgCurrency)}
              </div>
              <p className={`text-xs mt-1 ${kpis.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {kpis.variance >= 0 ? 'Under budget' : 'Over budget'}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <FolderOpen className="h-4 w-4 text-purple-600" />Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-700">{projectPerformance.length}</div>
              <p className="text-xs text-purple-600 mt-1">{kpis.approved} approved budget(s)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredBudgets.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <BarChart3 className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="font-medium text-muted-foreground">No data to display</p>
            <p className="text-sm text-muted-foreground mt-1">
              {budgets.length === 0
                ? 'Create budgets in Budget & Cost Control to see analytics here.'
                : 'No budgets match the selected filters.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main analytics tabs */}
      {!isLoading && filteredBudgets.length > 0 && (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="risks">Risk</TabsTrigger>
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />Monthly Trend
                  </CardTitle>
                  <CardDescription>Budget vs actual spend by month</CardDescription>
                </CardHeader>
                <CardContent>
                  {monthlyTrends.length === 0 ? (
                    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No monthly data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={monthlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" fontSize={12} />
                        <YAxis tickFormatter={shortAmount} fontSize={11} />
                        <Tooltip formatter={(v: number) => formatCurrency(v, orgCurrency)} />
                        <Legend />
                        <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                        <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <PieChartIcon className="w-5 h-5" />Spending by Project
                      </CardTitle>
                      <CardDescription>Distribution of actual expenditure</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleExportCategoryPDF} disabled={exportingCategory} title="Export to PDF">
                      {exportingCategory
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <FileText className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {categoryData.length === 0 ? (
                    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No spending recorded yet</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={categoryData} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ value }) => `${value}%`}>
                            {categoryData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${value}%`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1.5 mt-3">
                        {categoryData.map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                              <span className="truncate max-w-[160px]">{c.name}</span>
                            </span>
                            <span className="text-muted-foreground">{formatCurrency(c.amount, orgCurrency)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Project Performance Summary</CardTitle>
                <CardDescription>Budget utilization across projects</CardDescription>
              </CardHeader>
              <CardContent>
                {projectPerformance.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No project data available</p>
                ) : (
                  <div className="space-y-4">
                    {projectPerformance.map((project) => {
                      const pct = project.budget > 0 ? Math.min(100, (project.actual / project.budget) * 100) : 0;
                      const status = pct >= 100 ? 'Over Budget' : pct >= 90 ? 'Near Limit' : 'On Track';
                      return (
                        <div key={project.id} className="rounded-lg border border-border p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium">{project.name}</h4>
                            <Badge variant={status === 'On Track' ? 'default' : status === 'Near Limit' ? 'secondary' : 'destructive'}>
                              {status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                            <div>
                              <p className="text-muted-foreground">Budget</p>
                              <p className="font-medium">{formatCurrency(project.budget, orgCurrency)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Spent</p>
                              <p className="font-medium">{formatCurrency(project.actual, orgCurrency)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Utilization</p>
                              <p className="font-medium">{pct.toFixed(1)}%</p>
                            </div>
                          </div>
                          <Progress
                            value={Math.min(100, pct)}
                            className={`h-1.5 ${pct >= 100 ? '[&>div]:bg-red-500' : pct >= 90 ? '[&>div]:bg-amber-500' : ''}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Financial ── */}
          <TabsContent value="financial" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Cumulative Spend vs Budget</CardTitle>
                <CardDescription>Running total of budget allocation and actual expenditure over time</CardDescription>
              </CardHeader>
              <CardContent>
                {cumulativeTrend.length === 0 ? (
                  <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart data={cumulativeTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis tickFormatter={shortAmount} fontSize={11} width={70} />
                      <Tooltip formatter={(v: number) => formatCurrency(v, orgCurrency)} />
                      <Legend />
                      <Line type="monotone" dataKey="cumBudget" stroke="#3b82f6" strokeWidth={2} name="Cumulative Budget" dot={false} />
                      <Line type="monotone" dataKey="cumActual" stroke="#ef4444" strokeWidth={2} name="Cumulative Actual" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Cost Variance by Project</CardTitle></CardHeader>
                <CardContent>
                  {projectPerformance.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No data</p>
                  ) : (
                    <div className="space-y-3">
                      {projectPerformance.map((p) => {
                        const v = p.budget - p.actual;
                        return (
                          <div key={p.id} className="flex justify-between items-center p-3 border rounded-lg">
                            <span className="text-sm font-medium truncate max-w-[160px]">{p.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-sm font-medium ${v < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {v >= 0 ? '+' : ''}{formatCurrency(v, orgCurrency)}
                              </span>
                              {v < 0
                                ? <TrendingDown className="w-4 h-4 text-red-600" />
                                : <TrendingUp className="w-4 h-4 text-green-600" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Budget Distribution</CardTitle></CardHeader>
                <CardContent>
                  {projectPerformance.length === 0 ? (
                    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(200, projectPerformance.length * 40)}>
                      <BarChart data={projectPerformance} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={shortAmount} fontSize={11} />
                        <YAxis dataKey="name" type="category" fontSize={11} width={100} />
                        <Tooltip formatter={(v: number) => formatCurrency(v, orgCurrency)} />
                        <Legend />
                        <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                        <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Performance ── */}
          <TabsContent value="performance" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Budget Utilization</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-center py-4">{kpis.utilization.toFixed(1)}%</div>
                  <Progress
                    value={Math.min(100, kpis.utilization)}
                    className={`h-2 ${kpis.utilization >= 100 ? '[&>div]:bg-red-500' : kpis.utilization >= 75 ? '[&>div]:bg-amber-500' : ''}`}
                  />
                  <p className="text-xs text-center text-muted-foreground mt-2">of total budget spent</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Committed vs Actual</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3 py-2">
                    {[
                      { label: 'Committed', value: kpis.totalCommitted, color: '[&>div]:bg-orange-500' },
                      { label: 'Actual', value: kpis.totalActual, color: '[&>div]:bg-red-500' },
                      { label: 'Budget', value: kpis.totalBudget, color: '' },
                    ].map(({ label, value, color }) => (
                      <div key={label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span>{label}</span>
                          <span>{formatCurrency(value, orgCurrency)}</span>
                        </div>
                        <Progress
                          value={kpis.totalBudget > 0 ? Math.min(100, (value / kpis.totalBudget) * 100) : 0}
                          className={`h-1.5 ${color}`}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Budget Health</CardTitle></CardHeader>
                <CardContent className="py-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Approved</span><Badge className="bg-green-600">{kpis.approved}</Badge></div>
                    <div className="flex justify-between"><span>Total Budgets</span><span className="font-medium">{kpis.count}</span></div>
                    <div className="flex justify-between"><span>Projects Covered</span><span className="font-medium">{projectPerformance.length}</span></div>
                    <div className="flex justify-between">
                      <span>Avg. Utilization</span>
                      <span className={`font-medium ${kpis.utilization >= 100 ? 'text-red-600' : kpis.utilization >= 75 ? 'text-amber-600' : 'text-green-600'}`}>
                        {kpis.utilization.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />Budget Performance Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projectPerformance.map((p) => {
                    const pct = p.budget > 0 ? Math.min(100, (p.actual / p.budget) * 100) : 0;
                    return (
                      <div key={p.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/40">
                        <div className="w-32 truncate text-sm font-medium">{p.name}</div>
                        <div className="flex-1">
                          <Progress
                            value={pct}
                            className={`h-2 ${pct >= 100 ? '[&>div]:bg-red-500' : pct >= 75 ? '[&>div]:bg-amber-500' : ''}`}
                          />
                        </div>
                        <div className="text-sm font-medium w-12 text-right">{pct.toFixed(0)}%</div>
                        <div className="text-xs text-muted-foreground w-24 text-right">
                          {formatCurrency(p.actual, orgCurrency)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Risk ── */}
          <TabsContent value="risks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Budget Risk Assessment</CardTitle>
                <CardDescription>Budgets approaching or exceeding their limits</CardDescription>
              </CardHeader>
              <CardContent>
                {riskData.filter((r: any) => r.pct >= 75).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <CheckCircle className="h-8 w-8 text-green-600 mb-2" />
                    <p className="text-muted-foreground">All budgets are within acceptable limits</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {riskData
                      .filter((r: any) => r.pct >= 75)
                      .map((risk: any, i: number) => (
                        <div key={i} className="rounded-lg border border-border p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-medium text-sm">{risk.name}</h4>
                              <p className="text-xs text-muted-foreground">{risk.project}</p>
                            </div>
                            <Badge variant={risk.level === 'Over Budget' || risk.level === 'High' ? 'destructive' : 'secondary'}>
                              {risk.level}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <Progress
                              value={Math.min(100, risk.pct)}
                              className={`h-2 flex-1 ${risk.pct >= 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'}`}
                            />
                            <span className="text-sm font-medium w-12 text-right">{risk.pct.toFixed(0)}%</span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {riskData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>All Budget Utilization</CardTitle>
                  <CardDescription>Full list sorted by spend percentage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {riskData.map((risk: any, i: number) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-sm truncate w-44">{risk.name}</span>
                        <Progress
                          value={Math.min(100, risk.pct)}
                          className={`h-1.5 flex-1 ${risk.pct >= 100 ? '[&>div]:bg-red-500' : risk.pct >= 75 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'}`}
                        />
                        <span className="text-xs text-muted-foreground w-10 text-right shrink-0">{risk.pct.toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
